import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ThemeProps, SelectableThemeProps } from '@theme/base';
import moment from 'moment';
import { Dictionary, groupBy, flatten, map } from 'lodash';
import { ISO_8601_DATE_FORMAT, MONTH_FORMAT, LONG_MONTH_YEAR_FORMAT } from '@model/common';
import { Icons } from '@model/common/icons';
import { useI18NextContext } from '@components/hooks';
import { Icon } from '@components/common/generic-icon';
import { Pill } from '@components/search/common/Pill';
import { Checkbox, CheckboxLabel, CheckboxResponse } from '@components/common/checkbox';
import { TestId } from '@components/test-ids';
import { monthIsDisabled } from '@model/search';

const Container = styled.div<any>(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  paddingTop: theme.custom.spacing.medium * 1.5,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  flexDirection: 'column',
  height: '100%'
}));

const Navigation = styled.div<any>(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: theme.custom.spacing.large
}));

interface StyledChevronProps extends ThemeProps {
  disabled: boolean;
}
const StyledChevron = styled<any>(Icon)(({ theme, disabled }: StyledChevronProps) => ({
  fontSize: '1.4rem',
  cursor: disabled ? 'cursor' : 'pointer',
  color: disabled ? theme.custom.colors.group4.light : theme.custom.colors.group4.base,
  padding: theme.custom.spacing.small,
  margin: -theme.custom.spacing.small,
  ':hover': {
    color: disabled ? theme.custom.colors.group4.light : theme.custom.colors.group7.base
  }
}));

const PillContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%'
});

interface StyledPillProps extends ThemeProps {
  even: boolean;
}
const StyledPill: any = styled<any>(Pill)(({ theme, even }: StyledPillProps) => ({
  marginBottom: theme.custom.spacing.small,
  display: 'inline-box',
  width: `calc(50% - ${theme.custom.spacing.xSmall}px)`,
  marginLeft: even ? 0 : theme.custom.spacing.xSmall,
  marginRight: even ? theme.custom.spacing.xSmall : 0
}));

interface YearProps extends SelectableThemeProps {
  isMonthOnly?: boolean;
}
const Year: any = styled.div(({ theme, isSelected, isMonthOnly }: YearProps) => ({
  ...(theme.custom.typography.paragraph as any),
  fontWeight: 500,
  ...(isMonthOnly && {
    cursor: 'pointer',
    borderBottom: '3px solid transparent'
  }),
  ...(isSelected && {
    color: theme.custom.colors.group1.base,
    borderBottomColor: theme.custom.colors.group1.base
  })
}));

const CheckboxWrapper = styled.div({
  textAlign: 'center'
});

const StyledCheckboxLabel: any = styled(CheckboxLabel)(({ theme, isSelected }: SelectableThemeProps) => ({
  fontSize: '1.5rem',
  ...(isSelected && { color: theme.custom.colors.group1.base })
}));

export interface MonthsProps {
  month: string | Array<string>;
  onChange: (month: string) => void;
  onClear: () => void;
  isMonthOnly?: boolean;
  allSelected: boolean;
}

export function getMonths(date: moment.Moment): Dictionary<Array<string>> {
  const months: Array<string> = [];
  const dateStart = date.clone().startOf('year');
  const dateEnd = date.clone().add(2, 'year').endOf('year');
  const interim = dateStart.clone();
  while (dateEnd > interim || interim.format('M') === dateEnd.format('M')) {
    months.push(interim.format(ISO_8601_DATE_FORMAT));
    interim.add(1, 'month');
  }
  return groupBy(months, (month: string) => moment(month).year());
}

export const Months = ({ month, onChange, onClear, isMonthOnly, allSelected }: MonthsProps) => {
  const t: any = useI18NextContext();

  const months: Dictionary<Array<string>> = getMonths(moment());
  const years = Object.keys(months);

  const monthIsArray = Array.isArray(month);
  const monthMoment: Array<moment.Moment> = monthIsArray
    ? month.map((m) => moment(m || undefined))
    : [moment(month || undefined)];
  const initialIndex: number = month ? years.indexOf(moment.min(monthMoment).year().toString()) : 0;

  const [index, setIndex] = useState<number>(Math.max(initialIndex, 0));
  const year: string = years[index];
  const isFirst: boolean = year === years[0];
  const isLast: boolean = year === years[years.length - 1];

  const getMonthFormat = (month: string) => month.split('-').slice(0, 2).join('-');

  const onPillClick = (current: string) => {
    const clickedMonth = getMonthFormat(current);
    onChange(clickedMonth);
  };

  const renderPill = (curr: string, index: number) => {
    const currMoment = moment(curr);
    const value: string = currMoment.format(isMonthOnly ? LONG_MONTH_YEAR_FORMAT : MONTH_FORMAT);
    const selected: boolean = !!monthMoment.find((m) => m.isSame(curr));
    const isDisabled: boolean = monthIsDisabled(curr);

    return (
      <StyledPill
        key={value}
        value={value}
        selected={selected}
        disabled={isDisabled}
        onClick={() => {
          onPillClick(curr);
        }}
        even={index % 2 == 0}
      />
    );
  };

  const handleCheckboxChange = ({ checked }: CheckboxResponse) => {
    if (checked) {
      const newValue = map(flatten(Object.values(months)), getMonthFormat);
      onChange(newValue.join(','));
    } else {
      onClear();
    }
  };

  return (
    <Container>
      <Navigation>
        <StyledChevron name={Icons.CHEVRON_LEFT} disabled={isFirst} onClick={() => setIndex(Math.max(index - 1, 0))} />
        {years.map((year, idx) => (
          <Year
            data-testid={TestId.dealFinder.months.yearTab}
            key={year}
            isSelected={idx === index}
            onClick={() => setIndex(idx)}
            isMonthOnly
          >
            {year}
          </Year>
        ))}
        <StyledChevron name={Icons.CHEVRON_RIGHT} disabled={isLast} onClick={() => setIndex(index + 1)} />
      </Navigation>
      <PillContainer data-testid={TestId.dealFinder.months.pillContainer}>
        {months[years[index]].map((curr: string, index: number) => renderPill(curr, index))}
      </PillContainer>
      {isMonthOnly && (
        <CheckboxWrapper>
          <Checkbox
            value={allSelected}
            labelComponent={
              <StyledCheckboxLabel isSelected={allSelected}>{t('tour-search__date--any')}</StyledCheckboxLabel>
            }
            checked={allSelected}
            onChange={handleCheckboxChange}
          />
        </CheckboxWrapper>
      )}
    </Container>
  );
};
